import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { onSnapshot } from "firebase/firestore";
import { getSignedIn } from "../reducks/users/selectors";
import {
  fetchIsUpdateAvailable,
  getVersionRef,
  selfBumpVersion,
} from "../functions/appVersion/versionGate";
import { BUILD_ID } from "../generated/buildInfo";
import {
  isNewerBuildAvailable,
  shouldEnforceVersionGate,
  shouldRecheckNow,
} from "../functions/appVersion/versionGateCore";

// 新ビルドの公開を検知するフック。検知経路は3系統:
//  1. 初回ロード(Chrome起動・セッション復元タブの初活性化を含む)
//  2. onSnapshotによるリアルタイム購読(開いている間のプッシュ通知)
//  3. タブ復帰・フォーカス・回線復帰イベントでのサーバ強制確認
//     (バックグラウンド中にブラウザが購読を凍結していても、戻った瞬間に必ず確認する)
// ここで検知できなくても、注文確定・カート追加はトランザクション内の検査で最終的に止まる。
export const useAppVersionGate = () => {
  const isSignedIn = useSelector(getSignedIn);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const lastCheckedAtRef = useRef(null);

  useEffect(() => {
    if (!isSignedIn || !shouldEnforceVersionGate(window.location.hostname)) {
      return undefined;
    }

    // 新ビルドの初回ロードならバージョンdocを進める(失敗してもアプリ動作には影響させない)
    selfBumpVersion().catch((error) => {
      console.error("バージョンdocの更新に失敗しました:", error);
    });

    // 経路1・2: 購読(初回スナップショットが初回ロード時の確認を兼ねる)
    const unsubscribe = onSnapshot(
      getVersionRef(),
      (snap) => {
        if (
          snap.exists() &&
          isNewerBuildAvailable(snap.data().build_id, BUILD_ID)
        ) {
          setIsUpdateAvailable(true);
        }
      },
      (error) => {
        console.error("バージョンdocの購読に失敗しました:", error);
      }
    );

    // 経路3: タブ復帰・フォーカス・bfcache復元・回線復帰でサーバへ強制確認
    const recheck = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      const now = Date.now();
      if (!shouldRecheckNow(now, lastCheckedAtRef.current)) {
        return;
      }
      lastCheckedAtRef.current = now;
      fetchIsUpdateAvailable()
        .then((hasUpdate) => {
          if (hasUpdate) {
            setIsUpdateAvailable(true);
          }
        })
        .catch(() => {
          // オフライン等で失敗しても無視する(onlineイベントで再確認される)
        });
    };
    window.addEventListener("focus", recheck);
    window.addEventListener("pageshow", recheck);
    window.addEventListener("online", recheck);
    document.addEventListener("visibilitychange", recheck);

    return () => {
      unsubscribe();
      window.removeEventListener("focus", recheck);
      window.removeEventListener("pageshow", recheck);
      window.removeEventListener("online", recheck);
      document.removeEventListener("visibilitychange", recheck);
    };
  }, [isSignedIn]);

  return {
    isUpdateAvailable,
    reloadNow: () => window.location.reload(),
  };
};
